import type { Component, PresentationCurrent, TextValue } from '@/api/core/native-presentation';
import { languageLabel } from '../media/media-presentation';
import { KIND_TITLE, purposeLabel } from './presentation-categories';

export interface Change {
  id: string;
  label: string;
  before: string;
  after: string;
}

const visibility = (v: boolean | null | undefined) =>
  v === false ? '숨김' : v === true ? '표시' : '기본값';
const valueText = (value: string | null | undefined) =>
  value === null || value === undefined ? '미설정' : value === '' ? '빈 문구' : value;

/** 수정 폼이 원본과 비교하는 기준 — 연결된 사진·묶음 참조 문구는 이 폼에서 다루지 않는다. */
export const editableTextsOf = (current: PresentationCurrent) =>
  current.texts.filter((t) => !t.group_ref && !t.media_ref && !t.source_item_key);

/**
 * 영향 확인에 보일 목록 — 실제로 바뀐 것만 담는다.
 * 문구는 원본과 같은 순서로 놓인 항목끼리 비교하고(원본보다 뒤는 새 문구), 공간 안내도 같은 방식이다.
 */
export function describeChanges({
  current,
  name,
  texts,
  removed,
  components,
  removedComponents,
}: {
  current: PresentationCurrent;
  name: string;
  texts: TextValue[];
  removed: number[];
  components: Component[];
  removedComponents: string[];
}): Change[] {
  const changes: Change[] = [];
  if (name !== (current.native_name ?? '')) {
    changes.push({
      id: 'native-name',
      label: '관리 이름',
      before: valueText(current.native_name),
      after: valueText(name),
    });
  }
  const originals = editableTextsOf(current);
  const titleOf = (key: string | null | undefined) => {
    if (!key) return null;
    const own = current.texts.find(
      (t) =>
        t.component_key === key &&
        (t.purpose === 'COMPONENT_NAME' || t.purpose === 'STATION_NAME') &&
        t.value,
    );
    return own?.value ?? null;
  };
  const componentOf = (key: string | null | undefined) =>
    key ? components.find((c) => c.key === key) : undefined;
  const textLabel = (t: TextValue) => {
    const owner = componentOf(t.component_key);
    const prefix = owner
      ? `${KIND_TITLE[owner.kind]} ${titleOf(owner.key) ?? ''}`.trim() + ' · '
      : '';
    return `${prefix}${purposeLabel(t.purpose)} · ${languageLabel(t.language)}`;
  };
  texts.forEach((t, i) => {
    const original = originals[i];
    const gone =
      removed.includes(i) || (!!t.component_key && removedComponents.includes(t.component_key));
    if (!original) {
      if (!gone)
        changes.push({
          id: `text-${String(i)}`,
          label: textLabel(t),
          before: '새 문구',
          after: valueText(t.value),
        });
      return;
    }
    if (gone) {
      changes.push({
        id: `text-${String(i)}`,
        label: textLabel(t),
        before: valueText(original.value),
        after: '제거',
      });
    } else if (original.value !== t.value) {
      changes.push({
        id: `text-${String(i)}`,
        label: textLabel(t),
        before: valueText(original.value),
        after: valueText(t.value),
      });
    }
  });
  components.forEach((c, i) => {
    const original = current.components[i];
    const title = titleOf(c.key);
    const label = `${KIND_TITLE[c.kind]} ${title ?? String(i + 1)}`;
    if (!original) {
      changes.push({ id: `component-${String(i)}`, label, before: '새 안내', after: '추가' });
      return;
    }
    if (c.key && removedComponents.includes(c.key)) {
      changes.push({ id: `component-${String(i)}`, label, before: '사용 중', after: '제거' });
      return;
    }
    const before: string[] = [];
    const after: string[] = [];
    (
      [
        ['is_active', '사용'],
        ['show_on_detail', '상세'],
        ['show_on_search', '목록'],
      ] as const
    ).forEach(([flag, text]) => {
      if ((original[flag] ?? null) !== (c[flag] ?? null)) {
        before.push(`${text} ${visibility(original[flag])}`);
        after.push(`${text} ${visibility(c[flag])}`);
      }
    });
    if ((original.source_order ?? null) !== (c.source_order ?? null)) {
      before.push(`순서 ${String(original.source_order ?? '미설정')}`);
      after.push(`순서 ${String(c.source_order ?? '미설정')}`);
    }
    if (c.kind === 'LOCATION_LINE' && (original.line_code ?? null) !== (c.line_code ?? null)) {
      before.push(`노선 ${original.line_code ?? '미설정'}`);
      after.push(`노선 ${c.line_code ?? '미설정'}`);
    }
    if (before.length)
      changes.push({
        id: `component-${String(i)}`,
        label,
        before: before.join(' · '),
        after: after.join(' · '),
      });
  });
  return changes;
}
